/* global globalThis */
import { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { logout } from "../../redux/slices/authSlice";
import { persistor } from "../../redux/store";
import PropTypes from "prop-types";
import "./noRole.css";

import LucyIcon from "../../assets/lucy.svg";
import { useMsal } from "@azure/msal-react";
import { Avatar } from "@mui/material";

const NoRoleHeader = ({ hideProfileIcon = false }) => {
  const dispatch = useDispatch();
  const [showProfileCard, setShowProfileCard] = useState(false);
  const { instance } = useMsal();
  const photo = localStorage.getItem("profilePhoto");
  const userDetails = JSON.parse(localStorage.getItem("userDetails"));
  const profileRef = useRef(null);

  const handleLogout = () => {
    const accounts = instance.getAllAccounts();
    const activeAccount = instance.getActiveAccount();
    const account = accounts?.[0] || activeAccount;

    const logoutRequest = {
      account: account || undefined,
      postLogoutRedirectUri: `/landing`,
      // Clear storage only at the moment of redirect so the app doesn't flash /landing first
      onRedirectNavigate: () => {
        // Make browser Back from the accounts page land on /landing
        globalThis.history.replaceState(null, "", "/landing");
        persistor.purge();
        localStorage.clear();
        sessionStorage.clear();
        return true;
      },
    };

    instance.logoutRedirect(logoutRequest).catch((error) => {
      console.warn("Logout redirect failed:", error);
      dispatch(logout());
      globalThis.location.href = `/landing`;
    });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setShowProfileCard(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const stringAvatar = (name = "") => {
    const parts = name.trim().split(" ");
    const initials =
      (parts[0]?.[0] || "").toUpperCase() + (parts[1]?.[0] || "").toUpperCase();
    return {
      sx: {
        bgcolor: "#403c6f",
        color: "#fff",
        width: 40,
        height: 40,
        fontWeight: 600,
        fontSize: "16px",
      },
      children: initials || "?",
    };
  };

  const userFullName = `${userDetails?.firstName || ""} ${
    userDetails?.lastName || ""
  }`.trim();
  return (
    <>
      <div className="app-header">
        <div
          className="noheader-content"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <div
            className="noheader-left"
            style={{
              position: "relative",
              display: "flex",
              alignItems: "center",
            }}
          >
            <img
              src={LucyIcon}
              alt="Lucy Logo"
              className="lucy-logo"
              style={{
                height: "42px",
                width: "auto",
                marginRight: "16px",
              }}
            />
          </div>

          {!hideProfileIcon && (
            <div className="header-right" ref={profileRef}>
              <button
                type="button"
                className="profile"
                aria-haspopup="true"
                aria-expanded={showProfileCard}
                aria-label={
                  showProfileCard ? "Hide profile menu" : "Show profile menu"
                }
                onClick={() => setShowProfileCard((prev) => !prev)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setShowProfileCard((prev) => !prev);
                  }
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  position: "relative",
                  background: "transparent",
                  border: "none",
                  padding: 0,
                }}
              >
                {photo ? (
                  <img
                    src={photo}
                    alt="User"
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <Avatar {...stringAvatar(userFullName)} />
                )}
              </button>

              {showProfileCard && (
                <div
                  className="profile-card"
                  style={{
                    position: "absolute",
                    right: 0,
                    top: "50px",
                    backgroundColor: "#F7F8FB",
                    borderRadius: "12px",
                    boxShadow: "0 4px 16px rgba(0, 0, 0, 0.1)",
                    padding: "16px",
                    minWidth: "380px",
                    maxWidth: "450px",
                    width: "max-content",
                    zIndex: 900,
                  }}
                >
                  <div
                    style={{
                      backgroundColor: "#fff",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "16px",
                      borderRadius: "8px",
                      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
                      minHeight: "80px",
                      gap: "20px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        flex: 1,
                        minWidth: 0,
                      }}
                    >
                      {photo ? (
                        <img
                          src={photo}
                          alt="Profile"
                          style={{
                            width: 48,
                            height: 48,
                            borderRadius: "50%",
                            objectFit: "cover",
                            marginRight: "12px",
                            flexShrink: 0,
                          }}
                        />
                      ) : (
                        <Avatar
                          {...stringAvatar(userFullName)}
                          sx={{
                            width: 48,
                            height: 48,
                            marginRight: "12px",
                            bgcolor: "#403c6f",
                            flexShrink: 0,
                          }}
                        />
                      )}
                      <div style={{ flex: 1, minWidth: 0, maxWidth: "280px" }}>
                        <div
                          style={{
                            fontWeight: 600,
                            fontSize: "16px",
                            color: "#2E2E2E",
                            wordWrap: "break-word",
                            lineHeight: "1.3",
                          }}
                        >
                          {userDetails?.firstName} {userDetails?.lastName}
                        </div>
                        <div
                          style={{
                            fontSize: "14px",
                            color: "#6B7280",
                            marginTop: "2px",
                            wordWrap: "break-word",
                          }}
                        >
                          User Role - {userDetails?.primaryRoleName || "N/A"}
                        </div>
                        <div
                          style={{
                            fontSize: "13px",
                            color: "#6B7280",
                            marginTop: "2px",
                            wordWrap: "break-word",
                          }}
                        >
                          {userDetails?.emailId || userDetails?.email || ""}
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={handleLogout}
                      style={{
                        backgroundColor: "#fff",
                        color: "#f44336",
                        border: "1px solid #f44336",
                        borderRadius: "8px",
                        padding: "10px 16px",
                        fontWeight: 600,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        transition: "0.2s",
                        height: "40px",
                        minWidth: "80px",
                        flexShrink: 0,
                        whiteSpace: "nowrap",
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.backgroundColor = "#fff5f5";
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.backgroundColor = "#fff";
                      }}
                      onFocus={(e) => {
                        e.currentTarget.style.backgroundColor = "#fff5f5";
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.backgroundColor = "#fff";
                      }}
                    >
                      Logout
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="noheader-divider"></div>
    </>
  );
};

NoRoleHeader.propTypes = {
  hideProfileIcon: PropTypes.bool,
};

export default NoRoleHeader;
